import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO_URL, CONTACT } from "@/data/content";
import { LANGUAGES } from "@/i18n/translations";
import { useLanguage } from "@/i18n/LanguageContext";

const NAV_HREFS = ["#expertises", "#pourquoi", "#apropos", "#realisations", "#contact"];
const NAV_KEYS = ["expertises", "pourquoi", "apropos", "realisations", "contact"];

function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid="lang-switcher"
      className="flex items-center border border-slate-200 rounded-full overflow-hidden bg-white"
    >
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-btn-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`${compact ? "px-3 py-1.5" : "px-2.5 py-1"} font-mono-tech text-[11px] font-semibold tracking-wider transition-colors duration-200 ${
            lang === l.code ? "bg-[#E31E24] text-white" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Header({ scrollTo }) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            scrollTo(0);
          }}
          data-testid="header-logo-link"
          className="flex items-center gap-3 shrink-0"
        >
          <img src={LOGO_URL} alt="ACOMEP" className="h-10 w-auto" />
          <span className="hidden sm:block font-mono-tech text-[10px] tracking-[0.3em] text-slate-500 uppercase border-l border-slate-200 pl-3">
            {t.header.tagline}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
          {NAV_HREFS.map((href, i) => (
            <a
              key={href}
              href={href}
              onClick={go(href)}
              data-testid={`nav-link-${href.slice(1)}`}
              className="text-sm font-medium text-slate-600 hover:text-[#E31E24] transition-colors duration-200"
            >
              {t.nav[NAV_KEYS[i]]}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher />
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone-btn"
            className="flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-[#E31E24] transition-colors"
          >
            <Phone size={15} strokeWidth={2.5} />
            {CONTACT.phone}
          </a>
          <a
            href="#contact"
            onClick={go("#contact")}
            data-testid="nav-devis-btn"
            className="bg-[#E31E24] hover:bg-[#C7181D] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
          >
            {t.header.devis}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <LangSwitcher />
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="p-2 text-slate-900"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-200"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {NAV_HREFS.map((href, i) => (
                <a
                  key={href}
                  href={href}
                  onClick={go(href)}
                  data-testid={`mobile-nav-link-${href.slice(1)}`}
                  className="py-3 px-2 text-base font-medium text-slate-800 border-b border-slate-100"
                >
                  {t.nav[NAV_KEYS[i]]}
                </a>
              ))}
              <a
                href="#contact"
                onClick={go("#contact")}
                data-testid="mobile-nav-devis-btn"
                className="mt-3 bg-[#E31E24] text-white text-center font-semibold px-5 py-3 rounded-full"
              >
                {t.header.devis}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
